import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Icon, Tooltip, ButtonField, SrOnly } from '@wk/components-v3-react16';
import { ServerDashboardJob } from '../../dashboardType';
import { useDownloadLog } from './useDownloadLog';
import { useModalState } from './useModalState';
import ModalTable from './ModalTable/ModalTable';
import styles from './LogDataCell.module.scss';

const LogDataCell = (
  properties: CustomCellRendererProps<ServerDashboardJob>
) => {
  const jobId = properties.data?.jobId;
  const startTime = properties.data?.startTime;

  const { isModalOpen, logs, fileName, openModal, closeModal } =
    useModalState();

  const { handleDownloadLog, handleViewLog, isDownloading, isLoadingLog } =
    useDownloadLog({
      jobId,
      onViewSuccess: openModal,
      startTime,
    });

  const [viewTooltipOpen, setViewTooltipOpen] = useState(false);
  const [downloadTooltipOpen, setDownloadTooltipOpen] = useState(false);

  if (!jobId || !startTime) {
    return null;
  }

  const viewId = `view-log-${jobId}`;
  const downloadId = `download-log-${jobId}`;

  return (
    <div className={styles.container}>
      <ButtonField type="icon">
        <button
          type="button"
          id={viewId}
          className={styles.iconWrapper}
          onClick={handleViewLog}
          onMouseEnter={() => setViewTooltipOpen(true)}
          onMouseLeave={() => setViewTooltipOpen(false)}
          aria-label="View Log"
          disabled={isLoadingLog}
          style={{ color: '#005B92' }}
        >
          {isLoadingLog ? (
            <Icon name="wk-icon-refresh" isSpin={true} />
          ) : (
            <Icon name="wk-icon-visible" />
          )}
          <SrOnly>View Log</SrOnly>
        </button>
      </ButtonField>

      <ButtonField type="icon">
        <button
          type="button"
          id={downloadId}
          className={styles.iconWrapper}
          onClick={handleDownloadLog}
          onMouseEnter={() => setDownloadTooltipOpen(true)}
          onMouseLeave={() => setDownloadTooltipOpen(false)}
          aria-label="Download Log"
          disabled={isDownloading}
          style={{ color: '#005B92', fontSize: 24 }}
        >
          {isDownloading ? (
            <Icon name="wk-icon-refresh" isSpin={true} />
          ) : (
            <Icon name="wk-icon-download-line" />
          )}
          <SrOnly>Download Log</SrOnly>
        </button>
      </ButtonField>
      {createPortal(
        <Tooltip
          targetSelector={`#${viewId}`}
          isOpen={viewTooltipOpen}
          position="top"
        >
          View Log
        </Tooltip>,
        document.querySelector('#root') as HTMLElement
      )}

      {createPortal(
        <Tooltip
          targetSelector={`#${downloadId}`}
          isOpen={downloadTooltipOpen}
          position="top"
        >
          Download Log
        </Tooltip>,
        document.querySelector('#root') as HTMLElement
      )}

      {createPortal(
        <ModalTable
          isOpen={isModalOpen}
          logs={logs}
          fileName={fileName}
          onClose={closeModal}
          onDownload={handleDownloadLog}
          isDownloading={isDownloading}
        />,
        document.querySelector('#root') as HTMLElement
      )}
    </div>
  );
};

export default LogDataCell;
